import React from 'react';
import { useField, useFormikContext } from 'formik';
import { Checkbox, Field, type CheckboxProps, type FieldConfig } from '@kolking/react-ui';

import { useSpec } from './YupSpecContext';

interface FormCheckboxProps<TChecked, TUnchecked> extends FieldConfig, CheckboxProps {
  name: string;
  checkedValue?: TChecked;
  uncheckedValue?: TUnchecked;
}

export function FormCheckbox<TChecked = true, TUnchecked = false>({
  name,
  label,
  className,
  disabled = false,
  readOnly,
  required,
  checkedValue,
  uncheckedValue,
  ...props
}: FormCheckboxProps<TChecked, TUnchecked>) {
  const { isSubmitting, submitCount, setFieldValue } = useFormikContext();
  const [{ value, ...field }, meta] = useField<TChecked | TUnchecked | undefined>(name);
  const error = meta.touched || submitCount ? meta.error : undefined;
  const spec = useSpec();
  const handleChange = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      void setFieldValue(
        name,
        e.currentTarget.checked ? (checkedValue ?? true) : (uncheckedValue ?? false),
      );
    },
    [name, checkedValue, uncheckedValue, setFieldValue],
  );

  return (
    <Field className={className}>
      <Checkbox
        {...props}
        {...field}
        name={name}
        label={spec.label(name, label)}
        value="true"
        checked={value === (checkedValue ?? true)}
        disabled={spec.isDisabled(name, disabled || isSubmitting)}
        readOnly={spec.isReadOnly(name, readOnly)}
        required={spec.isRequired(name, required)}
        error={error}
        onChange={handleChange}
      />
    </Field>
  );
}
